import { Link } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { findSpecial } from "@/mocks/specials";
import Badge from "@/components/base/Badge";
import DramaCard from "@/components/base/DramaCard";
import SectionHeading from "@/components/base/SectionHeading";
import { formatDate } from "@/utils/catalog";

const latest = [...dramas].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 6);
const hottest = [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 4);
const featuredSpecial = findSpecial("quiet-voices");

export default function LatestSection() {
  return (
    <section className="border-y border-background-200 bg-background-100/60">
      <div className="container-page py-14 md:py-20">
        <SectionHeading
          kicker="New Arrivals"
          title="最新更新"
          desc="按更新日期排列，编辑部会为每一部新上线的作品补上一段观看提示。"
          action={
            <Link
              to="/category/ethics-drama"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
            >
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          }
        />

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {latest.slice(0, 4).map((drama) => (
            <DramaCard key={drama.id} drama={drama} variant="list" />
          ))}
        </div>
      </div>
    </section>
  );
}

export function HotSection() {
  return (
    <section className="container-page py-14 md:py-20">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <SectionHeading
            kicker="Editor's Selection"
            title="热门推荐"
            desc="根据站内人工精选与读者回访数据整理，每月更新一次。"
          />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {hottest.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <Badge tone="accent">本月专题</Badge>
              <span className="font-label text-[10px] tracking-[0.18em] text-foreground-400">SPECIAL</span>
            </div>
            {featuredSpecial ? (
              <Link to={`/special/${featuredSpecial.id}`} className="group mt-4 block">
                <div className="aspect-[16/10] w-full overflow-hidden rounded-md bg-background-200">
                  <img
                    src={featuredSpecial.cover}
                    alt={`${featuredSpecial.title} 专题主图`}
                    title={`${featuredSpecial.title} 推荐专题`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-800">
                  {featuredSpecial.title}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{featuredSpecial.lead}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-accent-700">
                  进入专题 <i className="ri-arrow-right-line" />
                </span>
              </Link>
            ) : null}
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-secondary-50 p-5">
            <p className="rule-label text-secondary-800">编辑部提示</p>
            <p className="mt-3 text-xs leading-relaxed text-foreground-700">
              站内所有条目均为虚构演示内容。你可以用收藏与浏览记录功能标记感兴趣的条目，数据只保存在你自己的浏览器里。
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

export function UpdateTimeline() {
  const rows = latest.slice(2, 6);
  return (
    <section className="container-page pb-14 md:pb-20">
      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-base font-semibold text-foreground-950">更新日志</h3>
          <span className="text-xs text-foreground-500">按日期倒序 · 共 {dramas.length} 部条目</span>
        </div>
        <ul>
          {rows.map((drama) => (
            <li key={drama.id} className="border-b border-background-200 last:border-0">
              <Link
                to={`/detail/${drama.id}`}
                className="group flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3.5 transition-colors hover:bg-background-100/70"
              >
                <span className="font-label text-[11px] text-foreground-400">{formatDate(drama.updatedAt)}</span>
                <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-800">
                  {drama.title}
                </span>
                <span className="text-xs text-foreground-500">{drama.types.join(" / ")}</span>
                <span className="ml-auto text-xs text-accent-700">{drama.rating.toFixed(1)} 分</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}